import { Col, Form, Input as AntInput, InputRef } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

import { FormItem } from '../Form';

type FormItemOTPProps = {
  name?: NamePath;
  length?: number;
  required?: boolean;
  disabled?: boolean;
  autoSubmit?: boolean;
  onComplete?: (code: string) => void;
  colSpan?: number;
  label?: string;
};

const OTPContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 20px 0;
`;

const OTPInput = styled(AntInput)<{ hasError?: boolean }>`
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: bold;
  border: 2px solid ${(props) => (props.hasError ? '#ff4d4f' : '#d9d9d9')};
  border-radius: 8px;

  &:focus {
    border-color: ${(props) => (props.hasError ? '#ff4d4f' : '#1890ff')};
    box-shadow: 0 0 0 2px
      ${(props) =>
        props.hasError ? 'rgba(255, 77, 79, 0.2)' : 'rgba(24, 144, 255, 0.2)'};
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  &.shake {
    animation: shake 0.5s;
  }

  @keyframes shake {
    0%,
    100% {
      transform: translateX(0);
    }
    10%,
    30%,
    50%,
    70%,
    90% {
      transform: translateX(-5px);
    }
    20%,
    40%,
    60%,
    80% {
      transform: translateX(5px);
    }
  }
`;

export function FormItemOTP(props: FormItemOTPProps) {
  const {
    name,
    length = 6,
    required = false,
    disabled = false,
    autoSubmit = false,
    onComplete,
    colSpan = 24,
    label,
  } = props;

  const form = Form.useFormInstance();
  const [otp, setOtp] = React.useState<string[]>(Array(length).fill(''));
  const [hasError, setHasError] = React.useState(false);
  const inputRefs = useRef<(InputRef | null)[]>([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setHasError(false);

    // Atualiza o valor no formulário
    const code = newOtp.join('');
    if (name) {
      form.setFieldsValue({ [name as string]: code });
    }

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (code.length === length) {
      if (autoSubmit && onComplete) {
        onComplete(code);
      }
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    if (!/^\d+$/.test(pastedData)) return;

    const pastedArray = pastedData.slice(0, length).split('');
    const newOtp = [...otp];

    pastedArray.forEach((char: string, idx: number) => {
      if (idx < length) {
        newOtp[idx] = char;
      }
    });

    setOtp(newOtp);
    setHasError(false);

    // Atualiza o valor no formulário
    const code = newOtp.join('');
    if (name) {
      form.setFieldsValue({ [name as string]: code });
    }

    const lastFilledIndex = Math.min(pastedArray.length - 1, length - 1);
    inputRefs.current[lastFilledIndex]?.focus();

    if (code.length === length && autoSubmit && onComplete) {
      onComplete(code);
    }
  };

  const triggerError = () => {
    setHasError(true);
    inputRefs.current.forEach((input) => {
      input?.input?.classList.add('shake');
    });
    setTimeout(() => {
      inputRefs.current.forEach((input) => {
        input?.input?.classList.remove('shake');
      });
    }, 500);
  };

  return (
    <Col span={colSpan}>
      <FormItem
        name={name}
        label={label}
        rules={[
          {
            required,
            message: 'Código é obrigatório',
          },
          {
            validator: (_, value) => {
              if (required && (!value || value.length !== length)) {
                return Promise.reject(
                  new Error(`Código deve ter ${length} dígitos`)
                );
              }
              if (value && value.length > 0 && value.length !== length) {
                return Promise.reject(
                  new Error(`Código deve ter ${length} dígitos`)
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <AntInput type="hidden" style={{ display: 'none' }} />
      </FormItem>

      <OTPContainer>
        {otp.map((digit, index) => (
          <OTPInput
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            maxLength={1}
            disabled={disabled}
            hasError={hasError}
            inputMode="numeric"
            pattern="\d*"
          />
        ))}
      </OTPContainer>
    </Col>
  );
}

export default FormItemOTP;
